import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Folder, FolderPlus, Upload, Image, Film, Trash2, Edit2, 
  ChevronRight, ArrowLeft, Grid, List, MoreVertical, X, Check, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { API, getAuthHeaders } from '../App';

export default function MediaStudio() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Dialogs
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');

  // Check auth
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/ru/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (!['Admin', 'ContentCreator'].includes(parsedUser.role)) {
      toast.error('Доступ запрещён');
      navigate('/ru');
      return;
    }
    
    setUser(parsedUser);
    setLoading(false);
  }, [navigate]);

  // Fetch folders and files
  const fetchContent = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/media/folders`, {
          params: { parent_id: currentFolder },
          headers: getAuthHeaders()
        }),
        axios.get(`${API}/media/gallery`, {
          params: { folder_id: currentFolder || '' },
          headers: getAuthHeaders()
        })
      ]);
      
      if (results[0].status === 'fulfilled') setFolders(results[0].value.data);
      if (results[1].status === 'fulfilled') setFiles(results[1].value.data.items || []);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        navigate('/ru/login');
      }
    }
  }, [currentFolder, getAuthHeaders, navigate]);

  useEffect(() => {
    if (user) {
      fetchContent();
    }
  }, [user, fetchContent]);

  // Navigate to folder
  const openFolder = async (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs([...breadcrumbs, folder]);
  };

  // Navigate back
  const goBack = () => {
    if (breadcrumbs.length === 0) return;
    const newBreadcrumbs = [...breadcrumbs];
    newBreadcrumbs.pop();
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolder(newBreadcrumbs.length > 0 ? newBreadcrumbs[newBreadcrumbs.length - 1].id : null);
  };

  // Navigate to breadcrumb
  const goToBreadcrumb = (index) => {
    if (index === -1) {
      setBreadcrumbs([]);
      setCurrentFolder(null);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1].id);
    }
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await axios.post(`${API}/media/folders`, {
        name: newFolderName,
        parent_id: currentFolder
      }, { headers: getAuthHeaders() });
      
      toast.success('Папка создана');
      setShowNewFolder(false);
      setNewFolderName('');
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания папки');
    }
  };

  // Rename folder
  const handleRenameFolder = async () => {
    if (!editFolderName.trim() || !editingFolder) return;
    
    try {
      await axios.put(`${API}/media/folders/${editingFolder.id}`, {
        name: editFolderName
      }, { headers: getAuthHeaders() });
      
      toast.success('Папка переименована');
      setEditingFolder(null);
      setEditFolderName('');
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Удалить папку "${folder.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/media/folders/${folder.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Папка удалена');
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  // Upload files
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    let uploaded = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (currentFolder) {
        formData.append('folder_id', currentFolder);
      }
      
      try {
        await axios.post(`${API}/media/upload`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        uploaded++;
        setUploadProgress(Math.round((uploaded / files.length) * 100));
      } catch (error) {
        toast.error(`Ошибка загрузки: ${file.name}`);
      }
    }
    
    setUploading(false);
    toast.success(`Загружено ${uploaded} файлов`);
    fetchContent();
    e.target.value = '';
  };

  // Delete file
  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Удалить "${file.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/media/gallery/${file.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Файл удалён');
      fetchContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  // Download file
  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Скачивание началось');
    } catch (error) {
      // Fallback: open in new tab
      window.open(file.url, '_blank');
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/ru/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image className="w-8 h-8 text-emerald-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Media Studio</h1>
              <p className="text-sm text-gray-500">Ainanan</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{user?.full_name || user?.email}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Выйти
            </Button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Breadcrumbs */}
            <button 
              onClick={() => goToBreadcrumb(-1)}
              className="text-sm text-gray-600 hover:text-emerald-600"
            >
              Главная
            </button>
            {breadcrumbs.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <button
                  onClick={() => goToBreadcrumb(index)}
                  className="text-sm text-gray-600 hover:text-emerald-600"
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewFolder(true)}
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Новая папка
            </Button>
            
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
              />
              <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? `${uploadProgress}%` : 'Загрузить'}
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Back button */}
        {breadcrumbs.length > 0 && (
          <button
            onClick={goBack}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Назад
          </button>
        )}

        {/* Folders */}
        {folders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-500 mb-3">Папки</h2>
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4"
              : "space-y-2"
            }>
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className={viewMode === 'grid'
                    ? "bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    : "bg-white rounded-lg border p-3 flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer group"
                  }
                >
                  <div 
                    className={viewMode === 'grid' ? "text-center" : "flex items-center gap-3"}
                    onClick={() => openFolder(folder)}
                  >
                    <Folder className={viewMode === 'grid' 
                      ? "w-12 h-12 text-amber-500 mx-auto mb-2"
                      : "w-6 h-6 text-amber-500"
                    } />
                    <span className="text-sm font-medium truncate">{folder.name}</span>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className={viewMode === 'grid' 
                        ? "absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                        : "p-1 hover:bg-gray-100 rounded"
                      }>
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => {
                        setEditingFolder(folder);
                        setEditFolderName(folder.name);
                      }}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Переименовать
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteFolder(folder)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Удалить
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Files */}
        {files.length > 0 && (
          <div>
            <h2 className="text-sm font-medium text-gray-500 mb-3">Файлы ({files.length})</h2>
            <div className={viewMode === 'grid'
              ? "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4"
              : "space-y-2"
            }>
              {files.map((file) => (
                <div
                  key={file.id}
                  className={viewMode === 'grid'
                    ? "bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow group relative"
                    : "bg-white rounded-lg border p-3 flex items-center justify-between hover:shadow-md transition-shadow"
                  }
                >
                  {viewMode === 'grid' ? (
                    <>
                      <div className="aspect-square bg-gray-100 relative">
                        {file.file_type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-12 h-12 text-gray-400" />
                          </div>
                        ) : (
                          <img 
                            src={file.url} 
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={() => handleDownload(file)}
                            className="p-1.5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600"
                            title="Скачать"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFile(file)}
                            className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                            title="Удалить"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-medium truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.file_type}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        {file.file_type === 'video' ? (
                          <Film className="w-10 h-10 text-gray-400" />
                        ) : (
                          <img 
                            src={file.url} 
                            alt={file.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-gray-500">{file.uploaded_by_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDownload(file)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded"
                          title="Скачать"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFile(file)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {folders.length === 0 && files.length === 0 && (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Папка пуста</h3>
            <p className="text-gray-500 mb-4">Создайте папку или загрузите файлы</p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => setShowNewFolder(true)}>
                <FolderPlus className="w-4 h-4 mr-2" />
                Новая папка
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleUpload}
                  className="hidden"
                />
                <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Загрузить
                  </span>
                </Button>
              </label>
            </div>
          </div>
        )}
      </main>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новая папка</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Название папки"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewFolder(false)}>
                Отмена
              </Button>
              <Button onClick={handleCreateFolder} className="bg-emerald-600 hover:bg-emerald-700">
                Создать
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={!!editingFolder} onOpenChange={() => setEditingFolder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Переименовать папку</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Новое название"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingFolder(null)}>
                Отмена
              </Button>
              <Button onClick={handleRenameFolder} className="bg-emerald-600 hover:bg-emerald-700">
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
